import {
  ChangeEventHandler,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { FileFieldProps } from './types';
import clsx from 'clsx';
import Field from '../Field';
import { Button, ButtonVariants } from 'stubben-edge-unified-frontend-react-components';
import { mergeRefs } from '../../../utils/refs';
import IconImage from '../../icons/IconImage';
import IconButton from '../IconButton';
import IconClose from '../../icons/IconClose';

/**
 *  UI Component
 */
const FileField = forwardRef<HTMLDivElement, FileFieldProps>(function FileField(
  props,
  ref
) {
  // Apply Default props values
  props = {
    ...{
      type: 'file',
      title: 'Click to browse for file',
      previewType: 'image'
    },
    ...props
  };
  if (!props.accept && props.type === 'image') props.accept = 'image/*';

  const {
    className,
    defaultValue,
    name,
    type,
    file,
    title,
    placeholder,
    previewType,
    onChange,
    onFocus,
    onBlur,
    inputRef,
    inputProps,
    showClearButton = true,
    hideSelectorText = true,
    ...rest
  } = props;

  const [focused, setFocused] = useState(false);

  const [previewUrl, setPreviewUrl] = useState<string>(
    defaultValue ? `${defaultValue}` : ''
  );
  const [fileName, setFileName] = useState<string | null>(
    file ? `${file.name}` : null
  );
  const [initValue, setInitValue] = useState(defaultValue);

  const localInputRef = useRef<HTMLInputElement>(null);

  const openSelector = () => {
    localInputRef?.current?.click();
  };

  const fetchPreviewForFile = (file: File) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onloadend = function (e) {
      setPreviewUrl(reader.result as string);
    };
  };

  const handleChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (e) => {
      const file = e.target.files && e.target.files[0];

      if (onChange) {
        onChange(file);
      }
    },
    [onChange]
  );

  const handleFocus = useCallback(() => {
    setFocused(true);
    if (onFocus) onFocus();
  }, [setFocused, onFocus]);

  const handleBlur = useCallback(() => {
    setFocused(false);
    if (onBlur) onBlur();
  }, [setFocused, onBlur]);

  const handleClear = useCallback(() => {
    setInitValue('');
    setPreviewUrl('');
    if (onChange) onChange(null);
  }, [onChange]);

  useEffect(() => {
    if (file) {
      fetchPreviewForFile(file);
      setFileName(file.name);
    } else if (!initValue || initValue === '') {
      setFileName('');
      setPreviewUrl('');
    } else if (defaultValue) {
      typeof defaultValue === 'string' && setPreviewUrl(defaultValue);
    }
  }, [file, initValue, defaultValue]);

  props.defaultValue = initValue;
  const cls = styleFileField(props);

  const inputElProps = {
    name,
    placeholder,
    className: clsx(cls.input),
    onChange: handleChange,
    onFocus: handleFocus,
    onBlur: handleBlur
  };
  return (
    <Field
      className={clsx(cls.main, className)}
      focused={focused}
      ref={ref}
      {...rest}
    >
      <input
        ref={mergeRefs([inputRef, localInputRef])}
        type="file"
        {...inputElProps}
        {...inputProps}
      />
      <div className={clsx(cls.selector)}>
        <Button
          variant={ButtonVariants.Secondary}
          className={clsx(cls.selectorButton)}
          onClick={openSelector}
        >
          {fileName || previewUrl || (initValue && initValue !== '')
            ? 'Change'
            : 'Upload'}
        </Button>
        {hideSelectorText && (
          <span className={clsx(cls.selectorText)}>
            <span>
              {initValue || file || previewUrl ? 'Click to change file' : title}
            </span>
            <span className={clsx(cls.filePath)}>
              {fileName ? fileName : initValue}
            </span>
          </span>
        )}
      </div>
      {type === 'image' && (
        <div className={clsx(cls.preview)}>
          {previewUrl ? <img src={previewUrl} alt={title} /> : <IconImage />}
        </div>
      )}
      {showClearButton && (fileName || initValue) && (
        <IconButton
          onClick={handleClear}
          title="Clear"
          aria-label="Clear"
          className={clsx(cls.clear)}
          variant="outlined"
          size="small"
        >
          <IconClose />
        </IconButton>
      )}
    </Field>
  );
});

export default FileField;

/**
 * Assign classNames to component based on properties
 */
function styleFileField(props: FileFieldProps) {
  const mc = 'FileField';
  const mcSelector = `${mc}__Selector`;
  const mcSelectorText = `${mc}__SelectorText`;
  const mcSelectorButton = `${mc}__SelectorButton`;
  const mcFilePath = `${mc}__FilePath`;
  const mcPreview = `${mc}__Preview`;
  const mcClear = `${mc}__Clear`;
  const cls = {
    main: [mc],
    input: ['Field__Input', `${mc}__Input`],
    selector: [mcSelector],
    selectorText: [mcSelectorText],
    selectorButton: [mcSelectorButton],
    filePath: [mcFilePath],
    preview: [mcPreview],
    clear: [mcClear]
  };
  if (props.file || props.defaultValue) {
    cls.main.push(`${mc}--isFilled`);
  }
  if (props.previewType === 'avatar') {
    cls.preview.push(`${mcPreview}--avatar`);
  }
  if (props.error || props.success) {
    cls.clear.push(`${mcClear}--withStatusIcon`);
  }
  return cls;
}
